import {
  ComponentIds,
  ConnectorsComponent,
  Entity,
  HierarchyUtils,
  PointSeriesConnector
} from '@inter-ikea-core/core';
import {
  CoreApi,
  InteriorComponent,
  Product,
  ProductInfoComponent,
  ProductType
} from '@inter-ikea-storageone/range';

export type NeededCoverCaps = {
  [productId: string]: number;
};

/**
 * Returns true if there aren't already cover caps in scene and there are frames that needs cover caps
 */
export const shouldShowCoverCapsSheet = (coreApi: CoreApi, products: Product[]) => {
  const coverCapProducts = products.filter(p => p.type === ProductType.PAX_COVER_CAPS);
  const coverCapsInScene = coreApi.entities.getByProductType(ProductType.PAX_COVER_CAPS);
  const neededCoverCaps = getNeededCoverCaps(coreApi, coverCapProducts);
  return (
    coverCapsInScene.length === 0 &&
    Object.keys(neededCoverCaps).some(productId => neededCoverCaps[productId] > 0)
  );
};

/**
 * Get all frames that has either no doors or glass doors (that can need cover caps)
 */
export const openOrGlassDoorFrames = (coreApi: CoreApi) => {
  const frames = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);

  return frames.filter(frame => {
    const doors = HierarchyUtils.getDescendantsWithComponent(frame, ComponentIds.DOOR_COMPONENT_ID);
    const slidingDoor = coreApi.entities.findEntityInHierarchyWithOneOfProductTypes(frame, [
      ProductType.PAX_SLIDING_DOOR_REGULAR_ASM,
      ProductType.PAX_SLIDING_DOOR_WITH_PANELS_ASM
    ]);
    if (slidingDoor) {
      doors.push(slidingDoor);
    }
    if (doors.length === 0) {
      return true;
    }
    const glassDoors = doors.filter(door => {
      const design = door.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.design;
      return design && design.includes('glass') && !design.includes('mirror');
    });
    return glassDoors.length > 0;
  });
};

/**
 * Different types of interiors covers different amout of holes which then don't
 * need cover caps
 */
const getCoveredHolesForInterior = (interior: Entity, frameDepth: number, coreApi: CoreApi) => {
  const interiorType = coreApi.entities.getProductType(interior);
  switch (interiorType) {
    case ProductType.PAX_DRAWER:
    case ProductType.PAX_DRAWER_FRAMED:
    case ProductType.PAX_DRAWER_FRAMED_GLASS:
    case ProductType.PAX_GLASS_DRAWER:
    case ProductType.PAX_MESH_BASKET_ASM:
    case ProductType.PAX_METAL_BASKET_ASM:
      return frameDepth === 580 ? 36 : 24;
    case ProductType.PAX_PULL_OUT_TRAY:
    case ProductType.PAX_PULL_OUT_TRAY_WITH_COMPARTMENT_INSERTS_ASM:
    case ProductType.PAX_PULL_OUT_TRAY_WITH_DIVIDER_INSERTS_ASM:
    case ProductType.PAX_PULL_OUT_TRAY_WITH_SHOE_INSERTS_ASM:
    case ProductType.PAX_PULL_OUT_TRAY_WITH_MAT_ASM:
      return frameDepth === 580 ? 12 : 8;
    default:
      return frameDepth === 580 ? 6 : 4;
  }
};

/**
 * Get a map of all cover caps product ids together with how many that is needed for the current scene
 */
export const getNeededCoverCaps = (
  coreApi: CoreApi,
  coverCapProducts: Product[]
): NeededCoverCaps => {
  const framesThatShouldHaveCoverCaps = openOrGlassDoorFrames(coreApi);
  const neededCoverCaps = framesThatShouldHaveCoverCaps.reduce(
    (neededCoverCaps: NeededCoverCaps, frame: Entity) => {
      const frameDesign = frame.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.design;
      const frameDepth = coreApi.entities.getEntityComSize(frame)?.depth || 0;
      const coverCapProductForFrame = coverCapProducts.find(
        product =>
          product.meta?.matchesColors &&
          frameDesign &&
          product.meta?.matchesColors.includes(frameDesign)
      );
      if (!coverCapProductForFrame) {
        return neededCoverCaps;
      }
      const holeSeriesConnector = frame
        .getComponent<ConnectorsComponent>(ComponentIds.CONNECTORS_COMPONENT_ID)
        ?.connectors.find(connector => connector.key === 'left-center-series');
      const nbrOfHoleConnectors = holeSeriesConnector
        ? (holeSeriesConnector as PointSeriesConnector).subConnectorCount
        : 0;
      if (nbrOfHoleConnectors === 0) return neededCoverCaps;
      const interiorsInFrame = HierarchyUtils.children(frame).filter(child =>
        child.hasComponent(InteriorComponent.id)
      );
      const nbrOfHoles = frameDepth === 580 ? nbrOfHoleConnectors * 6 : nbrOfHoleConnectors * 4;
      const coveredHoles = interiorsInFrame.reduce((acc, interior) => {
        return acc + getCoveredHolesForInterior(interior, frameDepth, coreApi);
      }, 0);

      neededCoverCaps[coverCapProductForFrame.coreProductId] =
        (neededCoverCaps[coverCapProductForFrame.coreProductId] || 0) + (nbrOfHoles - coveredHoles);
      return neededCoverCaps;
    },
    {}
  );
  //Cover caps are sold in packages of 100 so calculate number of packages needed
  Object.keys(neededCoverCaps).forEach(
    key => (neededCoverCaps[key] = Math.ceil(neededCoverCaps[key] / 100))
  );
  return neededCoverCaps;
};
